import React, { useEffect, useState } from "react";
import moment from "moment";
import { toast } from "react-toastify";
import DataTable from "react-data-table-component";
import { Alert, Box, Button, Chip, Tooltip, Typography } from "@mui/material";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import {
  JoinRequestChip,
  joinRequestDetail,
  joinRequestState,
} from "../../../../../commonComponents/JoinRequestStatus";
import {
  getJoinRequestsAPI,
  approveJoinRequestAPI,
  rejectJoinRequestAPI,
  joinRequestError,
} from "../../../../../../api/joinRequestsAPI";
import getEventItemsListAPIAdmin from "../../../../../../api/getEventItemsListAPIAdmin";
import { notifyJoinRequestsChanged } from "../../../../../../hooks/usePendingJoinRequests";
import ApproveDialog from "./ApproveDialog";
import RejectDialog from "./RejectDialog";

const FILTERS = [
  { status: "PENDING", label: "Waiting" },
  { status: "APPROVED", label: "Approved" },
  { status: "REJECTED", label: "Not accepted" },
  { status: "", label: "All" },
];

const nameOf = (row) =>
  [row.user?.firstName, row.user?.lastName].filter(Boolean).join(" ");

// "2 earlier" / "New", with the full history on hover
export const historyChip = (history) => {
  const auctions = history?.auctions || 0;
  if (!auctions) return <Chip label="New" size="small" variant="outlined" />;
  return (
    <Tooltip
      title={`Took part in ${auctions} of your earlier ${
        auctions === 1 ? "auction" : "auctions"
      }, accepted terms in ${history.acceptedTerms}`}
    >
      <Chip label={`${auctions} earlier`} size="small" color="primary" variant="outlined" />
    </Tooltip>
  );
};

// what a row opens to: contact, full company, note and history
export const RequestDetails = ({ data: row }) => {
  const company = row.company || {};
  const contact = company.designation
    ? `${company.contactName} (${company.designation})`
    : company.contactName;
  return (
    <Box
      sx={{
        px: 3,
        py: 1.5,
        backgroundColor: "#F8F9FC",
        fontSize: 14,
        lineHeight: 1.8,
        color: "#374151",
      }}
    >
      <div>
        {row.user?.email} · {row.user?.contactNumber}
      </div>
      <div>
        <strong>{company.companyName}</strong>
        {company.gstin ? ` · GSTIN ${company.gstin}` : ""}
        {company.pan ? ` · PAN ${company.pan}` : ""}
        {` · ${[company.city, company.state, company.pinCode].filter(Boolean).join(", ")}`}
      </div>
      <div>
        Contact: {contact} · {company.contactPhone} · {company.contactEmail}
      </div>
      {row.message && <div>Note: "{row.message}"</div>}
      <div>
        History:{" "}
        {row.history?.auctions
          ? `took part in ${row.history.auctions} of your earlier ${
              row.history.auctions === 1 ? "auction" : "auctions"
            }, accepted terms in ${row.history.acceptedTerms}`
          : "new to you"}
      </div>
      {row.status !== "PENDING" && joinRequestDetail(row) && (
        <div>{joinRequestDetail(row)}</div>
      )}
    </Box>
  );
};

// custom cells mark themselves so a click on them opens the row (the table
// only reacts to clicks on elements with this tag)
// The organiser's list of requests to participate in one auction: one short
// line per request (details open on click); approve (choosing the lots) or
// reject each one
function EditJoinRequests({ eventEditId, editLocked, allowJoinRequests, onGoToRules }) {
  const [filter, setFilter] = useState("PENDING");
  const [data, setData] = useState({ counts: {}, requests: [] });
  const [loading, setLoading] = useState(true);
  const [lots, setLots] = useState([]);
  const [approving, setApproving] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data: response } = await getJoinRequestsAPI(eventEditId, filter || undefined);
      setData(response);
    } catch (error) {
      toast(joinRequestError(error, "Couldn't load the requests."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventEditId, filter]);

  useEffect(() => {
    getEventItemsListAPIAdmin({ itemId: eventEditId })
      .then(({ data: response }) =>
        setLots(Array.isArray(response) ? response : response?.data || [])
      )
      .catch(() => setLots([]));
  }, [eventEditId]);

  const decided = (message) => {
    toast(message);
    setApproving(null);
    setRejecting(null);
    load();
    notifyJoinRequestsChanged();
  };

  const approve = async (eventItemIds) => {
    try {
      setSaving(true);
      await approveJoinRequestAPI(eventEditId, approving.eventJoinRequestId, eventItemIds);
      decided(
        eventItemIds.length
          ? `Approved with ${eventItemIds.length} ${eventItemIds.length === 1 ? "lot" : "lots"}.`
          : "Approved. Allot lots in View Lots & Buyer."
      );
    } catch (error) {
      toast(joinRequestError(error, "Couldn't approve the request."));
    } finally {
      setSaving(false);
    }
  };

  const reject = async (decision) => {
    try {
      setSaving(true);
      await rejectJoinRequestAPI(eventEditId, rejecting.eventJoinRequestId, decision);
      decided("Request rejected.");
    } catch (error) {
      toast(joinRequestError(error, "Couldn't reject the request."));
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      name: "Bidder",
      grow: 2,
      cell: (row) => (
        <Box data-tag="allowRowEvents" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <span data-tag="allowRowEvents" style={{ fontWeight: 500 }}>{nameOf(row)}</span>
          {row.message && (
            <Tooltip title={`"${row.message}"`}>
              <ChatBubbleOutlineIcon sx={{ fontSize: 16, color: "#4B5563" }} aria-label="Has a note" />
            </Tooltip>
          )}
        </Box>
      ),
    },
    {
      name: "Company",
      grow: 2,
      cell: (row) => (
        <Box data-tag="allowRowEvents" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <span data-tag="allowRowEvents">{row.company?.companyName}</span>
          {row.duplicateCompany && (
            <Tooltip title="Same PAN or GSTIN as another participant">
              <WarningAmberIcon sx={{ fontSize: 16, color: "#B45309" }} aria-label="Same company as another participant" />
            </Tooltip>
          )}
        </Box>
      ),
    },
    {
      name: "Lots",
      cell: (row) => (row.interestedLotNumbers ? row.interestedLotNumbers.join(", ") : "All"),
    },
    { name: "History", minWidth: "115px", cell: (row) => historyChip(row.history) },
    {
      name: "Requested",
      minWidth: "100px",
      selector: (row) => row.createdAt,
      format: (row) => moment(row.createdAt).format("D MMM"),
      sortable: true,
    },
    {
      name: "",
      grow: 2,
      minWidth: "200px",
      right: true,
      // the buttons don't open the row
      ignoreRowClick: true,
      cell: (row) =>
        row.status === "PENDING" ? (
          <Box sx={{ display: "flex", gap: 1, whiteSpace: "nowrap" }}>
            <Button
              variant="contained"
              size="small"
              disabled={editLocked}
              onClick={() => setApproving(row)}
              sx={{ backgroundColor: "#1E7B4A", borderRadius: 0, "&:hover": { backgroundColor: "#166B3F" } }}
            >
              ✓ Approve
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="error"
              disabled={editLocked}
              onClick={() => setRejecting(row)}
              sx={{ borderRadius: 0 }}
            >
              ✕ Reject
            </Button>
          </Box>
        ) : (
          <JoinRequestChip state={joinRequestState(row)} />
        ),
    },
  ];

  const counts = data.counts || {};
  const countFor = (status) =>
    status ? counts[status] || 0 : Object.values(counts).reduce((sum, n) => sum + n, 0);

  return (
    <Box>
      {allowJoinRequests === false && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            onGoToRules && (
              <Button size="small" onClick={onGoToRules}>
                Go to Rules
              </Button>
            )
          }
        >
          Bidders can't request to participate yet. Turn on "Allow bidders to
          request to participate" in the Rules tab.
        </Alert>
      )}

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
        {FILTERS.map((option) => (
          <Chip
            key={option.label}
            label={`${option.label} ${countFor(option.status)}`}
            onClick={() => setFilter(option.status)}
            color={filter === option.status ? "primary" : "default"}
            variant={filter === option.status ? "filled" : "outlined"}
          />
        ))}
      </Box>

      <DataTable
        columns={columns}
        data={data.requests || []}
        progressPending={loading}
        expandableRows
        expandOnRowClicked
        expandableRowsComponent={RequestDetails}
        pagination
        responsive
        dense
        noDataComponent={
          <Box sx={{ p: 3, color: "#4B5563" }}>
            {filter === "PENDING" ? "No requests waiting." : "No requests here."}
          </Box>
        }
      />
      <Typography variant="caption" sx={{ color: "#6B7280" }}>
        Click a request to see the bidder's contact and company details.
      </Typography>

      <ApproveDialog
        request={approving}
        lots={lots}
        onClose={() => setApproving(null)}
        onApprove={approve}
        saving={saving}
      />
      <RejectDialog
        request={rejecting}
        onClose={() => setRejecting(null)}
        onReject={reject}
        saving={saving}
      />
    </Box>
  );
}

export default EditJoinRequests;
